import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import toast from 'react-hot-toast';

export const PaymentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    platformFees: 0,
    driverEarnings: 0,
    ownerEarnings: 0
  });
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [recentRefunds, setRecentRefunds] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const [reconciliation, setReconciliation] = useState({ totalPayments: 0, totalPayouts: 0, totalRefunds: 0, balance: 0 });

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        // Fetch all completed rides (with finalFareCents)
        const ridesSnap = await getDocs(collection(db, 'rides'));
        const rides = ridesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const completedRides = rides.filter(r => r.status === 'completed' && r.finalFareCents);

        let totalRevenue = 0;
        let platformFees = 0;
        let driverEarnings = 0;
        let ownerEarnings = 0;
        // Calculate totals from ledger entries would be more accurate, but for simplicity:
        completedRides.forEach(ride => {
          totalRevenue += ride.finalFareCents;
          // Assuming splits: platformFee, driverShare, ownerShare stored in ride or ledger
          if (ride.platformFeeCents) platformFees += ride.platformFeeCents;
          if (ride.driverShareCents) driverEarnings += ride.driverShareCents;
          if (ride.ownerShareCents) ownerEarnings += ride.ownerShareCents;
        });
        setStats({
          totalRevenue: (totalRevenue / 100).toFixed(2),
          platformFees: (platformFees / 100).toFixed(2),
          driverEarnings: (driverEarnings / 100).toFixed(2),
          ownerEarnings: (ownerEarnings / 100).toFixed(2)
        });

        // Pending payouts (status = 'pending')
        const payoutsSnap = await getDocs(query(collection(db, 'payouts'), where('status', '==', 'pending'), orderBy('createdAt', 'desc'), limit(20)));
        const payouts = payoutsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPendingPayouts(payouts);

        // Recent refunds (status = 'completed' or 'pending')
        const refundsSnap = await getDocs(query(collection(db, 'refunds'), orderBy('createdAt', 'desc'), limit(20)));
        const refunds = refundsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRecentRefunds(refunds);

        // Failed payments (rides with paymentMethod = 'card' and status = 'cancelled' or 'failed')
        const failedRides = rides.filter(r => r.paymentMethod === 'card' && (r.status === 'cancelled' || r.status === 'failed'));
        setFailedPayments(failedRides.slice(0, 20));

        // Reconciliation summary (total payments from completed rides, total payouts, total refunds)
        const totalPayments = totalRevenue; // cents
        const totalPayouts = payouts.reduce((sum, p) => sum + (p.amountCents || 0), 0);
        const totalRefunds = refunds.reduce((sum, r) => sum + (r.amountCents || 0), 0);
        const balance = totalPayments - totalPayouts - totalRefunds;
        setReconciliation({
          totalPayments: (totalPayments / 100).toFixed(2),
          totalPayouts: (totalPayouts / 100).toFixed(2),
          totalRefunds: (totalRefunds / 100).toFixed(2),
          balance: (balance / 100).toFixed(2)
        });

      } catch (err) {
        console.error(err);
        toast.error('Failed to load payment data');
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentData();
  }, []);

  if (loading) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading payment data...</div></div>;

  return (
    <div className="space-y-6">
      {/* Revenue Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">R{stats.totalRevenue}</div>
          <div className="text-gray-400 text-sm">Total Revenue</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">R{stats.platformFees}</div>
          <div className="text-gray-400 text-sm">Platform Fees</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">R{stats.driverEarnings}</div>
          <div className="text-gray-400 text-sm">Driver Earnings</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">R{stats.ownerEarnings}</div>
          <div className="text-gray-400 text-sm">Owner Earnings</div>
        </div>
      </div>

      {/* Pending Payouts Table */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Pending Payouts</h2>
        {pendingPayouts.length === 0 ? (
          <p className="text-gray-400">No pending payouts.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-700">
                <tr><th className="px-4 py-2">Party</th><th className="px-4 py-2">Amount (R)</th><th className="px-4 py-2">Created</th><th className="px-4 py-2">Status</th></tr></thead>
              <tbody>
                {pendingPayouts.map(p => (
                  <tr key={p.id} className="border-t border-gray-700">
                    <td className="px-4 py-2">{p.partyType} {p.partyId?.slice(-6)}</td>
                    <td className="px-4 py-2">{(p.amountCents / 100).toFixed(2)}</td>
                    <td className="px-4 py-2">{p.createdAt?.toDate().toLocaleDateString()}</td>
                    <td className="px-4 py-2"><span className="text-yellow-400">Pending</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Refunds */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Refunds</h2>
        {recentRefunds.length === 0 ? <p className="text-gray-400">No refunds.</p> : (
          <table className="min-w-full text-sm"><thead className="bg-gray-700"><tr><th className="px-4 py-2">Ride ID</th><th className="px-4 py-2">Amount</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Created</th></tr></thead>
          <tbody>{recentRefunds.map(r => <tr key={r.id} className="border-t border-gray-700"><td className="px-4 py-2 font-mono text-xs">{r.rideId?.slice(-8)}</td><td className="px-4 py-2">R{(r.amountCents / 100).toFixed(2)}</td><td className="px-4 py-2">{r.status}</td><td className="px-4 py-2">{r.createdAt?.toDate().toLocaleDateString()}</td></tr>)}</tbody></table>)}
      </div>

      {/* Reconciliation Summary */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Reconciliation</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>Total Payments (R):</div><div className="font-mono">R{reconciliation.totalPayments}</div>
          <div>Total Payouts (R):</div><div className="font-mono">R{reconciliation.totalPayouts}</div>
          <div>Total Refunds (R):</div><div className="font-mono">R{reconciliation.totalRefunds}</div>
          <div className="font-semibold">Balance (R):</div><div className="font-mono font-semibold text-emerald-400">R{reconciliation.balance}</div>
        </div>
      </div>
    </div>
  );
};
